/**
 * SchemaList Component
 *
 * Left panel showing searchable list of API entities with icons.
 * Entities come from /metadata/entities endpoint.
 */

import React from 'react'
import { Input, List, Typography, Spin, Empty } from 'antd'
import {
  SearchOutlined,
  TableOutlined,
  UserOutlined,
  ShoppingOutlined,
  SettingOutlined,
  DatabaseOutlined,
  ApiOutlined,
  FileOutlined,
  TeamOutlined,
  DollarOutlined,
  SwapOutlined,
  BellOutlined,
  HistoryOutlined,
  LockOutlined,
  AppstoreOutlined
} from '@ant-design/icons'
import { getIconFromName } from './utils'
import type { SchemaListProps, ApiEntity } from './types'

const { Text } = Typography

/**
 * Check if path is similar enough to name that showing both is redundant.
 * e.g., "Attributions" and "/attributions" are similar (redundant)
 * e.g., "CustomFieldConfig" and "/field_configs" are different (show both)
 */
function isPathSimilarToName(name: string, path: string): boolean {
  // Normalize both for comparison
  const normalizedName = name.toLowerCase().replace(/[_\s-]/g, '')
  const normalizedPath = path.toLowerCase().replace(/^\//, '').replace(/[_\s-]/g, '')

  // Direct match (case-insensitive, ignoring underscores/spaces)
  if (normalizedName === normalizedPath) return true

  // Check if one is a simple plural/singular of the other
  if (normalizedName + 's' === normalizedPath) return true
  if (normalizedName === normalizedPath + 's') return true
  if (normalizedName + 'es' === normalizedPath) return true
  if (normalizedName === normalizedPath + 'es') return true

  return false
}

/**
 * Get icon for entity - prefer entity.icon from YAML, fallback to heuristic
 */
function getEntityIcon(entity: ApiEntity): React.ReactNode {
  // If entity has an icon defined in YAML, use it
  if (entity.icon) {
    return getIconFromName(entity.icon)
  }

  // Otherwise, use heuristic based on name/path
  const name = (entity.name + entity.path).toLowerCase()

  // User/Auth related
  if (name.includes('user') || name.includes('member')) return <UserOutlined />
  if (name.includes('org') || name.includes('team')) return <TeamOutlined />
  if (name.includes('role') || name.includes('permission') || name.includes('auth')) return <LockOutlined />

  // Business entities
  if (name.includes('offering') || name.includes('product') || name.includes('item')) return <ShoppingOutlined />
  if (name.includes('customer') || name.includes('vendor') || name.includes('supplier')) return <UserOutlined />
  if (name.includes('price') || name.includes('cost') || name.includes('currency')) return <DollarOutlined />

  // Data/Pipeline
  if (name.includes('pipeline') || name.includes('data_map') || name.includes('mapping')) return <SwapOutlined />
  if (name.includes('blob') || name.includes('file') || name.includes('upload')) return <FileOutlined />
  if (name.includes('connection') || name.includes('oauth') || name.includes('integration')) return <ApiOutlined />

  // System/Config
  if (name.includes('config') || name.includes('setting') || name.includes('field_config')) return <SettingOutlined />
  if (name.includes('audit') || name.includes('log') || name.includes('history')) return <HistoryOutlined />
  if (name.includes('notification') || name.includes('alert')) return <BellOutlined />

  // Default
  if (name.includes('table') || name.includes('data')) return <TableOutlined />

  return <DatabaseOutlined />
}


/**
 * SchemaList Component
 */
export function SchemaList({
  entities,
  selectedEntityPath,
  onSelect,
  isLoading,
  searchTerm,
  onSearchChange
}: SchemaListProps): React.ReactElement {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Search input */}
      <div style={{ padding: '12px', borderBottom: '1px solid #f0f0f0' }}>
        <Input
          placeholder="Search entities..."
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          allowClear
          size="small"
        />
      </div>

      {/* Entity list */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
            <Spin size="small" />
          </div>
        ) : entities.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={searchTerm ? 'No matching entities' : 'No entities found'}
            style={{ marginTop: '24px' }}
          />
        ) : (
          <List
            dataSource={entities}
            size="small"
            split={false}
            renderItem={(entity) => {
              const isSelected = entity.path === selectedEntityPath

              return (
                <List.Item
                  onClick={() => onSelect(entity)}
                  style={{
                    cursor: 'pointer',
                    padding: '8px 12px',
                    background: isSelected ? '#e6f7ff' : 'transparent',
                    borderLeft: isSelected ? '3px solid #1890ff' : '3px solid transparent',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.background = '#fafafa'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.background = 'transparent'
                    }
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    overflow: 'hidden'
                  }}>
                    <span style={{ color: isSelected ? '#1890ff' : '#8c8c8c', flexShrink: 0 }}>
                      {getEntityIcon(entity)}
                    </span>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <Text
                        style={{
                          display: 'block',
                          fontWeight: isSelected ? 500 : 400,
                          color: isSelected ? '#1890ff' : 'inherit',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {entity.name}
                      </Text>
                      {/* Only show path if it meaningfully differs from the name */}
                      {!isPathSimilarToName(entity.name, entity.path) && (
                        <Text
                          type="secondary"
                          style={{
                            display: 'block',
                            fontSize: '11px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {entity.path}
                        </Text>
                      )}
                    </div>
                  </div>
                </List.Item>
              )
            }}
          />
        )}
      </div>

      {/* Footer with count */}
      <div style={{
        padding: '8px 12px',
        borderTop: '1px solid #f0f0f0',
        background: '#fafafa',
        fontSize: '12px',
        color: '#8c8c8c'
      }}>
        <AppstoreOutlined style={{ marginRight: '6px' }} />
        {entities.length} entit{entities.length !== 1 ? 'ies' : 'y'}
        {searchTerm && ` (filtered)`}
      </div>
    </div>
  )
}

export default SchemaList
